import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@components/button';
import { EmptyStateCell } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { TabsHeader } from '@components/header/tabs-header';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDropdown } from '@components/icons/dropdown';
import { IconEmailAddress } from '@components/icons/email-address';
import { IconFormIndicatorInfoCurrentColor } from '@components/icons/form-indicator-info-current-color';
import { IconReadMore } from '@components/icons/read-more';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useEvent } from '@hooks/use-event';
import { styledTranslate, useScopedTranslate } from '@locale/translate';

import {
  useCheckEmailAddressUsage,
  useDeleteEmailAddress,
  useEmailDomains,
  useInvalidateEmailDomains,
  useSetDefaultEmailAddress,
  useUpdateEmailAddress,
} from 'api/company/hooks';
import {
  EmailAddress,
  EmailAddressWithDomain,
  EmailDomain as EmailDomainType,
  SMTP_AUTH_TYPE_BASIC,
  verifyEmailByToken,
} from 'api/company/requests';

import {
  AddressRow,
  BlockedAddressesTab,
  DnsDomainCard,
  LoadFailedPanel,
  SmtpDomainCard,
  SystemEmailCard,
} from './components';
import { useEditSenderNameModal } from './modals/edit-sender-name';
import { useEmailVerificationModal } from './modals/email-verification';
import { useFallbackEmailSelectionModal } from './modals/fallback-email-selection';
import { useManagedEmailDomainFormSidebar } from './sidebars/managed-form';
import { useUnmanagedEmailDomainFormSidebar } from './sidebars/unmanaged-form';

export const EmailDomain = () => {
  const { t } = useScopedTranslate(locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === BLOCKED_TAB_ID ? BLOCKED_TAB_ID : DOMAINS_TAB_ID;

  const { invalidate } = useInvalidateEmailDomains();
  const { showSuccess, showError } = useToaster();
  const verificationAttempted = useRef(false);
  const verifyToken = searchParams.get('verify');

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const runVerification = useEvent(() => {
    if (!verifyToken || verificationAttempted.current) return;

    verificationAttempted.current = true;

    verifyEmailByToken(verifyToken)
      .then((response) => {
        if (!isMountedRef.current) return;
        if (response.isVerified) {
          if (response.message?.includes('already')) {
            showSuccess(t('alreadyVerified'));
          } else {
            showSuccess(t('emailVerified'));
          }
          invalidate();
        } else {
          showError(t('verificationFailed'));
        }
      })
      .catch((error) => {
        if (!isMountedRef.current) return;
        const errorMessage = error.response?.data?.error || '';
        if (errorMessage.includes('expired')) {
          showError(t('verificationExpired'));
        } else {
          showError(t('verificationFailed'));
        }
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('verify');
            return next;
          },
          { replace: true }
        );
      });
  });

  useEffect(() => {
    runVerification();
  }, [verifyToken, runVerification]);

  return (
    <TabsHeader
      activeTab={activeTab}
      onTabChange={(tabId) => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (tabId === DOMAINS_TAB_ID) {
              next.delete('tab');
            } else {
              next.set('tab', tabId);
            }
            return next;
          },
          { replace: true }
        );
      }}
      actions={<HeaderActions />}
      title={
        <Stack align="center" justify="center" space={2} orientation="horizontal">
          <IconEmailAddress color="elementBlue100" />
          <Text
            font="bold18"
            color="textPrimary"
            text={styledTranslate({
              key: 'emailDomain',
              variables: {
                and: {
                  font: 'bold18',
                  text: t('and'),
                  color: 'textHelper',
                },
              },
              translations: locale,
            })}
          />
        </Stack>
      }
      tabs={[
        { id: DOMAINS_TAB_ID, label: t('domainsAndAddresses'), content: <DomainsTab /> },
        { id: BLOCKED_TAB_ID, label: t('blockedAddresses'), content: <BlockedAddressesTab /> },
      ]}
    />
  );
};

const DOMAINS_TAB_ID = 'domains';
const BLOCKED_TAB_ID = 'blocked';

const DomainsTab = () => {
  const { t } = useScopedTranslate(locale);
  const domainsQuery = useEmailDomains();

  const hasDomains = domainsQuery.data && domainsQuery.data.length > 0;
  const showError = domainsQuery.isError && !hasDomains;

  return (
    <PageContent>
      <PageContainers.MiddleColumnPage>
        <Stack space={6}>
          <Text text={t('description')} font="regular14" />

          {domainsQuery.isLoading && <RelativeSpinner />}

          {showError && (
            <LoadFailedPanel
              title={t('domainsLoadFailedTitle')}
              description={t('domainsLoadFailedDescription')}
              onRetry={() => domainsQuery.refetch()}
              isRetrying={domainsQuery.isFetching}
            />
          )}

          {!domainsQuery.isLoading && !showError && !hasDomains && <EmptyState />}

          {hasDomains && <DomainsList domains={domainsQuery.data!} />}
        </Stack>
      </PageContainers.MiddleColumnPage>
    </PageContent>
  );
};

const HeaderActions = () => {
  const { t } = useScopedTranslate(locale);
  const managedSidebar = useManagedEmailDomainFormSidebar();
  const unmanagedSidebar = useUnmanagedEmailDomainFormSidebar();

  return (
    <Stack orientation="horizontal" space={2}>
      <Button
        iconLeft={<IconReadMore />}
        text={t('core.learnMore')}
        href="https://help.journeys.50skills.app/en/articles/166925-sending-emails-from-your-company-email"
        variant="secondary"
      />
      <Menu
        buttonProps={{
          variant: 'primary',
          iconLeft: <IconAddPlus />,
          text: t('addCustomDomain'),
          iconRight: <IconDropdown />,
        }}
      >
        <Menu.Label label={t('selectMethod')} />
        <Menu.Item label={t('managed')} onSelect={() => managedSidebar()} />
        <Menu.Item label={t('unmanaged')} onSelect={() => unmanagedSidebar()} />
      </Menu>
    </Stack>
  );
};

const EmptyState = () => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={6}>
      <EmptyStateCell icon={<IconEmailAddress />} text={t('noCustomDomain')} />
      <Separator />
      <InfoBox
        variant="indigo"
        icon={<IconFormIndicatorInfoCurrentColor />}
        text={t('outgoingCommunications')}
      />
      <Separator />
      <SystemEmailCard />
    </Stack>
  );
};

type DomainsListProps = {
  domains: EmailDomainType[];
};

const DomainsList = ({ domains }: DomainsListProps) => {
  const { t } = useScopedTranslate(locale);
  const managedSidebar = useManagedEmailDomainFormSidebar();
  const unmanagedSidebar = useUnmanagedEmailDomainFormSidebar();
  const verificationModal = useEmailVerificationModal();
  const editSenderNameModal = useEditSenderNameModal();
  const fallbackModal = useFallbackEmailSelectionModal();
  const showDeleteModal = useDeleteModal();

  const setDefaultMutation = useSetDefaultEmailAddress();
  const updateAddressMutation = useUpdateEmailAddress();
  const deleteAddressMutation = useDeleteEmailAddress();
  const checkUsageMutation = useCheckEmailAddressUsage();
  const { invalidate } = useInvalidateEmailDomains();

  const dnsDomains = domains.filter((d) => d.isManaged);
  const smtpDomains = domains.filter((d) => !d.isManaged);

  const allAddresses: EmailAddressWithDomain[] = useMemo(() => {
    const addresses: EmailAddressWithDomain[] = [];
    domains.forEach((domain) => {
      domain.emailAddresses.forEach((address) => {
        addresses.push({
          ...address,
          domainType: domain.isManaged ? 'DNS' : 'SMTP',
        });
      });
    });
    return addresses;
  }, [domains]);

  const verifiedDomains = domains.filter((d) => d.isVerified);

  const handleEditDnsDomain = (domain: EmailDomainType) => {
    managedSidebar({ domain: domain });
  };

  const handleEditSmtpDomain = (domain: EmailDomainType) => {
    unmanagedSidebar({
      shortId: domain.shortId,
      smtpHost: domain.smtpHost ?? '',
      smtpPort: domain.smtpPort?.toString() ?? '587',
      smtpLogin: domain.smtpLogin ?? '',
      smtpAuthType: domain.smtpAuthType ?? SMTP_AUTH_TYPE_BASIC,
      isVerified: domain.isVerified,
    });
  };

  const handleAddAddress = () => {
    verificationModal({ domains: verifiedDomains });
  };

  const handleEditAddress = (address: EmailAddress) => {
    editSenderNameModal({ address });
  };

  const handleVerifyAddress = (address: EmailAddress) => {
    verificationModal({ address });
  };

  const handleSetDefaultAddress = (address: EmailAddress) => {
    setDefaultMutation.mutate(address.shortId);
  };

  const handleToggleActiveAddress = async (address: EmailAddressWithDomain) => {
    if (address.isActive) {
      const usage = await checkUsageMutation.mutateAsync(address.shortId);

      if (usage.inUse) {
        const availableEmails = allAddresses.filter(
          (a) => a.shortId !== address.shortId && a.isVerified && a.isActive
        );

        fallbackModal({
          mode: 'disable',
          availableEmails,
          onConfirm: async (fallbackShortId) => {
            await updateAddressMutation.mutateAsync({
              shortId: address.shortId,
              data: { isActive: false, fallbackShortId },
            });
            await invalidate();
          },
        });
        return;
      }
    }

    updateAddressMutation.mutate({
      shortId: address.shortId,
      data: { isActive: !address.isActive },
    });
  };

  const handleDeleteAddress = async (address: EmailAddressWithDomain) => {
    const usage = await checkUsageMutation.mutateAsync(address.shortId);

    if (usage.inUse) {
      const availableEmails = allAddresses.filter(
        (a) => a.shortId !== address.shortId && a.isVerified && a.isActive
      );

      fallbackModal({
        email: address,
        mode: 'remove',
        availableEmails,
        onConfirm: async (fallbackShortId) => {
          await deleteAddressMutation.mutateAsync({
            shortId: address.shortId,
            fallbackShortId,
          });
          await invalidate();
        },
      });
      return;
    }

    showDeleteModal({
      title: t('deleteAddressTitle'),
      description: t('deleteAddressDescription', address.fromAddress),
      hasUserConfirmation: true,
      buttonText: t('remove'),
      buttonIcon: <IconTrash />,
      onConfirm: async () => {
        await deleteAddressMutation.mutateAsync({ shortId: address.shortId });
        await invalidate();
      },
    });
  };

  return (
    <Stack space={6}>
      <Stack space={2}>
        <Text text={t('domains')} font="bold14" />
        <Stack space={2}>
          {dnsDomains.map((domain) => (
            <DnsDomainCard
              key={domain.shortId}
              domain={domain}
              onEditDomain={() => handleEditDnsDomain(domain)}
            />
          ))}
          {smtpDomains.map((domain) => (
            <SmtpDomainCard
              key={domain.shortId}
              domain={domain}
              onEditDomain={() => handleEditSmtpDomain(domain)}
            />
          ))}
        </Stack>
      </Stack>

      <Stack space={2}>
        <Text text={t('emailAddresses')} font="bold14" />
        {allAddresses.length > 0 && (
          <Stack space={2}>
            {allAddresses.map((address) => (
              <AddressRow
                key={address.shortId}
                address={address}
                domainType={address.domainType}
                onEdit={() => handleEditAddress(address)}
                onVerify={() => handleVerifyAddress(address)}
                onSetDefault={() => handleSetDefaultAddress(address)}
                onToggleActive={() => handleToggleActiveAddress(address)}
                onDelete={() => handleDeleteAddress(address)}
              />
            ))}
          </Stack>
        )}
        <Stack orientation="horizontal">
          <Button
            variant="secondary"
            text={t('addEmailAddress')}
            iconLeft={<IconAddPlus />}
            onClick={handleAddAddress}
            disabled={verifiedDomains.length === 0}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    domainsAndAddresses: 'Domains & addresses',
    blockedAddresses: 'Blocked addresses',
    domainsLoadFailedTitle: 'Could not load domains',
    domainsLoadFailedDescription: "We couldn't load your domains. Refresh the page or try again.",
    addCustomDomain: 'Add custom domain',
    selectMethod: 'Select method',
    managed: 'DNS (Managed by us)',
    unmanaged: 'SMTP (Managed by you)',
    emailDomain: 'Email domain {and} addresses',
    and: '&',
    noCustomDomain: 'No custom domains added',
    description:
      'Manage the email addresses you want to use for your outbound emails\nYour email domains control how your emails are sent through Journeys',
    outgoingCommunications:
      'All outgoing email communication will be sent from noreply@mg.50skills.app\n' +
      'To personalise your communication we recommend adding your own email domain',
    emailVerified: 'Email address verified successfully',
    alreadyVerified: 'This email address has already been verified',
    verificationFailed: 'Email verification failed. The link may be invalid.',
    verificationExpired: 'Verification link has expired. Please request a new one.',
    domains: 'Domains',
    emailAddresses: 'Email addresses',
    addEmailAddress: 'Add email address',
    deleteAddressTitle: 'Delete email address',
    deleteAddressDescription: 'Are you sure you want to delete the email address {0}?',
    remove: 'Remove',
  },
};
